import { trackEvent } from './analytics';

// Authentication service for user management
// Use the environment variable as the single source of truth for the API URL.
// Fallback to the production URL if the variable is not set.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://diary.tarotreadingcard.online';

// Debug logging
console.log('🔗 AuthService API URL:', API_BASE_URL);
console.log('🌐 Environment REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

class AuthService {
  constructor() {
    this.token = localStorage.getItem('authToken');
    this.user = this.token ? JSON.parse(localStorage.getItem('user') || '{}') : null;
  }

  // Register new user
  async register(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Registration failed');
      }

      // Store token and user data
      this.setAuthData(data.token, data.user);
      
      // Track analytics
      this.setAnalyticsUserId(this.user.id);
      trackEvent('sign_up', {
        method: 'email'
      });
      
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Login user
  async login(email, password) {
    try {
      // Get the current session ID to link existing readings
      const sessionId = this.getSessionId();
      
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, sessionId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Login failed');
      }

      // Store token and user data
      this.setAuthData(data.token, data.user);
      
      // Track analytics
      this.setAnalyticsUserId(this.user.id);
      trackEvent('login', {
        method: 'email'
      });

      // Log session readings linking result if available
      if (data.linkedReadings > 0) {
        console.log(`Successfully linked ${data.linkedReadings} session readings to user account`);
        trackEvent('session_readings_linked', {
          user_id: this.user.id,
          linked_count: data.linkedReadings
        });
      }
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Logout user
  logout() {
    // Track analytics before clearing data
    if (this.user) {
      trackEvent('logout', {
        user_id: this.user.id
      });
    }

    this.token = null;
    this.user = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    
    // Clear analytics user ID
    this.setAnalyticsUserId(null);
  }

  // Set authentication data
  setAuthData(token, user) {
    this.token = token;
    this.user = user;
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(user));
  }

  // Set analytics user ID
  setAnalyticsUserId(userId) {
    if (window.gtag) {
      window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
        user_id: userId
      });
    }
  }

  // Get current user
  getCurrentUser() {
    return this.user;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  // Get auth token
  getToken() {
    return this.token;
  }

  // Get session ID (for linking session readings to user account)
  getSessionId() {
    let sessionId = localStorage.getItem('tarot-session-id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('tarot-session-id', sessionId);
    }
    return sessionId;
  }

  // Get auth headers for API requests
  getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
    };
  }

  // Get user profile from server
  async getProfile() {
    try {
      if (!this.token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token is invalid, logout user
          this.logout();
          throw new Error('Session expired');
        }
        throw new Error(data.error || 'Failed to get profile');
      }

      // Update user data
      this.user = data.user;
      localStorage.setItem('user', JSON.stringify(this.user));

      return data;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  // Update user profile
  async updateProfile(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Profile update failed');
      }

      // Update stored user data
      this.user = data.user;
      localStorage.setItem('user', JSON.stringify(this.user));
      
      // Track analytics
      trackEvent('profile_updated', {
        fields_updated: Object.keys(userData)
      });
      
      return data;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  }

  // Change password
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/password`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Password change failed');
      }
      
      // Track analytics
      trackEvent('password_changed');
      
      return data;
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  }

  // Link session readings to user account
  async linkSessionReadings(sessionId) {
    try {
      if (!this.token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/link-session`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ sessionId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to link session readings');
      }

      // Track analytics
      trackEvent('session_readings_linked', {
        linked_count: data.linkedCount
      });

      return data;
    } catch (error) {
      console.error('Link session error:', error);
      throw error;
    }
  }

  // Get user's readings
  async getUserReadings(limit = 50, offset = 0) {
    try {
      if (!this.token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/api/diary/readings?limit=${limit}&offset=${offset}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get user readings');
      }

      return data;
    } catch (error) {
      console.error('Get user readings error:', error);
      throw error;
    }
  }

  // Get user's reading statistics
  async getUserStats() {
    try {
      if (!this.token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/api/diary/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get user statistics');
      }

      return data;
    } catch (error) {
      console.error('Get user stats error:', error);
      throw error;
    }
  }

  // Verify auth token with server
  async verifyToken() {
    try {
      await this.getProfile();
      return true;
    } catch (error) {
      if (error.message === 'Session expired') {
        return false;
      }
      throw error;
    }
  }

  // Request password reset
  async requestPasswordReset(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email');
      }
      
      return data;
    } catch (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
  }

  // Verify reset token validity
  async verifyResetToken(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-reset-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Invalid reset token');
      }
      
      return data;
    } catch (error) {
      console.error('Verify reset token error:', error);
      throw error;
    }
  }

  // Reset password with token
  async resetPassword(token, newPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }
      
      return data;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }
}

// Create singleton instance
const authService = new AuthService();
export default authService;
